import { useExternalIngestionFolderListForOrganizationQuery } from '@client/graphql/__generated__/main-operations';
import {
  ExternalIngestionFolderListRowFragment,
  TransactionPropertyType,
  TransactionSide,
  UsState,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '~/services/document/hooks';
import { useAppContext } from '~/services/main/contexts/AppContext';

interface OrganizationCompliancePageContextType {
  // Query state
  folders: ExternalIngestionFolderListRowFragment[];
  loading: boolean;
  error: Error | null | undefined;
  fetchTriggerRef: (ref: HTMLDivElement | null) => void;

  // Filter state
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  agentSearchTerm: string;
  setAgentSearchTerm: (term: string) => void;
  officeFilter: string[] | null;
  setOfficeFilter: (office: string[] | null) => void;
  statusFilter: string[] | null;
  setStatusFilter: (status: string[] | null) => void;
  sideFilter: TransactionSide | 'ALL';
  setSideFilter: (side: TransactionSide | 'ALL') => void;
  stateFilter: UsState | null;
  setStateFilter: (state: UsState | null) => void;
  propertyTypeFilter: TransactionPropertyType | null;
  setPropertyTypeFilter: (propertyType: TransactionPropertyType | null) => void;
  dateRangeStart: Date | null;
  setDateRangeStart: (date: Date | null) => void;
  dateRangeEnd: Date | null;
  setDateRangeEnd: (date: Date | null) => void;
  hasActiveFilters: boolean;

  // Organization data
  organizationId: string | null;
}

const OrganizationCompliancePageContext = createContext<
  OrganizationCompliancePageContextType | undefined
>(undefined);

interface OrganizationCompliancePageProviderProps {
  children: ReactNode;
}

export const OrganizationCompliancePageProvider: FC<
  OrganizationCompliancePageProviderProps
> = ({ children }) => {
  const { selectedOrganization } = useAppContext();
  const organizationId = selectedOrganization?.id || null;
  const { folderId } = useParams<{ folderId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const parseDateParam = (value: string | null): Date | null => {
    if (!value) return null;

    const exactDate = dayjs(value, 'YYYY-MM-DD', true);
    if (exactDate.isValid()) return exactDate.toDate();

    const fallback = dayjs(value);

    return fallback.isValid() ? fallback.toDate() : null;
  };

  const formatDateParam = (date: Date | null): string | null => {
    if (!date || isNaN(date.getTime())) return null;

    return dayjs(date).format('YYYY-MM-DD');
  };

  // Helper to parse array from URL params with legacy fallback
  const parseArrayParam = (
    params: URLSearchParams,
    key: string
  ): string[] | null => {
    const multiValues = params.getAll(key).filter(Boolean);
    if (multiValues.length > 0) return multiValues;

    const single = params.get(key);
    if (!single) return null;

    // Legacy delimiters: prefer | or ; to avoid breaking names with commas.
    const legacyDelimiters = ['|', ';'];
    for (const delimiter of legacyDelimiters) {
      if (single.includes(delimiter)) {
        const parsed = single.split(delimiter).filter(Boolean);
        if (parsed.length > 0) return parsed;
      }
    }

    // Last-resort legacy comma split only when it looks like an old multi-select
    // (no space after the comma). This keeps names like "Smith, Jones & Associates"
    // intact while still supporting previously stored "A,B" URLs.
    if (single.includes(',') && !single.includes(', ')) {
      const parsed = single.split(',').filter(Boolean);
      if (parsed.length > 0) return parsed;
    }

    return [single];
  };

  // Filter state - initialize from URL params
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('search') || ''
  );
  const [agentSearchTerm, setAgentSearchTerm] = useState(
    searchParams.get('agentSearch') || ''
  );
  const [officeFilter, setOfficeFilter] = useState<string[] | null>(
    parseArrayParam(searchParams, 'office')
  );
  const [statusFilter, setStatusFilter] = useState<string[] | null>(
    parseArrayParam(searchParams, 'status')
  );
  const [sideFilter, setSideFilter] = useState<TransactionSide | 'ALL'>(() => {
    const sideParam = searchParams.get('side');
    if (
      sideParam &&
      Object.values(TransactionSide).includes(sideParam as TransactionSide)
    ) {
      return sideParam as TransactionSide;
    }

    return 'ALL';
  });
  const [stateFilter, setStateFilter] = useState<UsState | null>(() => {
    const stateParam = searchParams.get('state');
    if (stateParam && Object.values(UsState).includes(stateParam as UsState)) {
      return stateParam as UsState;
    }

    return null;
  });
  const [propertyTypeFilter, setPropertyTypeFilter] =
    useState<TransactionPropertyType | null>(() => {
      const typeParam = searchParams.get('propertyType');
      if (
        typeParam &&
        Object.values(TransactionPropertyType).includes(
          typeParam as TransactionPropertyType
        )
      ) {
        return typeParam as TransactionPropertyType;
      }

      return TransactionPropertyType.RESIDENTIAL;
    });
  // Date range filter - default to last two weeks
  const [dateRangeStart, setDateRangeStart] = useState<Date | null>(() => {
    return parseDateParam(searchParams.get('startAt'));
  });
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(() => {
    return parseDateParam(searchParams.get('endAt'));
  });

  // Sync state changes with URL params
  useEffect(() => {
    // When viewing a specific folder, don't rewrite search params;
    // this prevents dashboard filter keys from stomping document-specific params.
    if (folderId) {
      return;
    }

    const params = new URLSearchParams(searchParams);

    const setParam = (key: string, value: string | null) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    };

    const setArrayParam = (key: string, values: string[] | null) => {
      params.delete(key);

      if (values && values.length > 0) {
        values.forEach((value) => params.append(key, value));
      }
    };

    setParam('search', searchTerm || null);
    setParam('agentSearch', agentSearchTerm || null);
    setArrayParam('office', officeFilter);
    setArrayParam('status', statusFilter);
    setParam('side', sideFilter !== 'ALL' ? sideFilter : null);
    setParam('state', stateFilter);
    setParam('propertyType', propertyTypeFilter);
    setParam('startAt', formatDateParam(dateRangeStart));
    setParam('endAt', formatDateParam(dateRangeEnd));

    const nextParamsString = params.toString();
    const currentParamsString = searchParams.toString();

    if (nextParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchTerm,
    agentSearchTerm,
    officeFilter,
    statusFilter,
    sideFilter,
    stateFilter,
    propertyTypeFilter,
    folderId,
    searchParams,
    setSearchParams,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // Query with filters
  const [_fetchTriggerRef, { data, loading, error }] = useInfiniteQuery(
    useExternalIngestionFolderListForOrganizationQuery,
    {
      variables: {
        input: {
          organizationId: organizationId!,
          // Include search term if provided
          ...(searchTerm && { search: searchTerm }),
          // Include agent search term if provided
          ...(agentSearchTerm && { agentSearch: agentSearchTerm }),
          // Include office filter if provided (now supports arrays)
          ...(officeFilter &&
            officeFilter.length > 0 && { offices: officeFilter }),
          // Include status filter if provided (now supports arrays)
          ...(statusFilter &&
            statusFilter.length > 0 && { statuses: statusFilter }),
          // Include transactionSide filter if not 'ALL'
          ...(sideFilter !== 'ALL' && { transactionSide: sideFilter }),
          // Include state filter if provided
          ...(stateFilter && { state: stateFilter }),
          // Include propertyType filter if provided
          ...(propertyTypeFilter && { propertyType: propertyTypeFilter }),
          // Include date range filters if provided
          ...(dateRangeStart && { createdAtStart: dateRangeStart }),
          ...(dateRangeEnd && { createdAtEnd: dateRangeEnd }),
        },
        // NOTE(mike): As of 2025-07-15, to specify ordering by the `folderCreatedAt` field,
        // useExternalIngestionFolderListForOrganizationQuery must also request the `folderCreatedAt`
        // field
        orderBy: '-folderCreatedAt',
        perPage: 10,
      },
      skip: !organizationId,
    }
  );

  const folders =
    data?.externalIngestionFolderListForOrganization?.results || [];

  // Create a wrapper function that handles null refs
  const fetchTriggerRef = (ref: HTMLDivElement | null) => {
    if (ref) {
      _fetchTriggerRef(ref);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    searchTerm ||
      agentSearchTerm ||
      (officeFilter && officeFilter.length > 0) ||
      (statusFilter && statusFilter.length > 0) ||
      sideFilter !== 'ALL' ||
      stateFilter ||
      propertyTypeFilter ||
      dateRangeStart ||
      dateRangeEnd
  );

  const contextValue: OrganizationCompliancePageContextType = {
    folders,
    loading,
    error,
    fetchTriggerRef,
    searchTerm,
    setSearchTerm,
    agentSearchTerm,
    setAgentSearchTerm,
    officeFilter,
    setOfficeFilter,
    statusFilter,
    setStatusFilter,
    sideFilter,
    setSideFilter,
    stateFilter,
    setStateFilter,
    propertyTypeFilter,
    setPropertyTypeFilter,
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,
    hasActiveFilters,
    organizationId,
  };

  return (
    <OrganizationCompliancePageContext.Provider value={contextValue}>
      {children}
    </OrganizationCompliancePageContext.Provider>
  );
};

export const useOrganizationCompliancePage = () => {
  const context = useContext(OrganizationCompliancePageContext);

  if (!context) {
    throw new Error(
      'useOrganizationCompliancePage must be used within an OrganizationCompliancePageProvider'
    );
  }

  return context;
};
